import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import debounce from 'lodash/debounce';
import Tooltip from 'react-scale/components/website/index/how-it-works/Tooltip';

export default class TaskResponseAnnotation extends Component {
  static propTypes = {
    annotations: PropTypes.array.isRequired,
    bgTransparent: PropTypes.bool,
    hideTooltip: PropTypes.bool,
    hover: PropTypes.bool,
    imageURL: PropTypes.string.isRequired,
    responsive: PropTypes.bool,
    imageZoomFactor: PropTypes.number,
    rounded: PropTypes.bool
  };

  static defaultProps = {
    bgTransparent: false,
    hideTooltip: false,
    hover: false,
    responsive: false,
    imageZoomFactor: 1,
    rounded: false
  };

  constructor(props) {
    super(props);

    this.setActiveAnnotation = this.setActiveAnnotation.bind(this);
    this.hideAnnotation = this.hideAnnotation.bind(this);
    this.showBoundingBoxes = this.showBoundingBoxes.bind(this);
    this.renderBoundingBox = this.renderBoundingBox.bind(this);
    this.onResize = this.onResize.bind(this);

    this.onResizeDebounced = debounce(this.onResize, 100);
  }

  state = {
    tooltip: { pageX: 0, pageY: 0, show: false, content: '' },
    showBoundingBoxes: false,
    alwaysShowLabels: false
  };

  shouldComponentUpdate(nextProps, nextState) {
    if (
      this.props.annotations !== nextProps.annotations ||
      this.props.bgTransparent !== nextProps.bgTransparent ||
      this.props.hideTooltip !== nextProps.hideTooltip ||
      this.props.hover !== nextProps.hover ||
      this.props.imageURL !== nextProps.imageURL ||
      this.props.responsive !== nextProps.responsive ||
      this.state.imageWidth !== nextState.imageWidth ||
      this.state.imageNaturalWidth !== nextState.imageNaturalWidth ||
      this.state.showBoundingBoxes !== nextState.showBoundingBoxes ||
      this.state.alwaysShowLabels !== nextState.alwaysShowLabels ||
      this.state.tooltip.pageX !== nextState.tooltip.pageX ||
      this.state.tooltip.pageY !== nextState.tooltip.pageY ||
      this.state.tooltip.show !== nextState.tooltip.show ||
      this.state.tooltip.content !== nextState.tooltip.content
    ) {
      return true;
    }
    return false;
  }

  componentDidMount() {
    this.setState({
      imageWidth: this.imageRef.width,
      imageNaturalWidth: this.imageRef.naturalWidth
    });
    window.addEventListener('resize', this.onResizeDebounced);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.onResizeDebounced);
  }

  onResize() {
    this.setState({
      imageWidth: this.imageRef.width,
      imageNaturalWidth: this.imageRef.naturalWidth
    });
  }

  setActiveAnnotation(annotation) {
    if (this.props.hideTooltip) return;

    return e => {
      const { top: pageY, left: pageX } = e.target.getBoundingClientRect();
      const { top: offsetY, left: offsetX } = this.content.getBoundingClientRect();

      const content = this.getTooltip(annotation);

      this.setState({
        tooltip: {
          pageX: pageX - offsetX,
          pageY: pageY - offsetY,
          show: true,
          content
        }
      });
    };
  }

  hideAnnotation() {
    this.setState(state => ({
      tooltip: { ...state.tooltip, show: false }
    }));
  }

  percentageCropToStyle(crop, offset = { top: 0, left: 0 }) {
    return {
      width: `${
        ((crop.width - 2) * this.imageRef.width) /
        (this.imageRef.naturalWidth / this.props.imageZoomFactor)
      }px`,
      height: `${
        ((crop.height - 2) * this.imageRef.width) /
        (this.imageRef.naturalWidth / this.props.imageZoomFactor)
      }px`,
      left: `${
        (crop.left * this.imageRef.width) /
          (this.imageRef.naturalWidth / this.props.imageZoomFactor) +
          offset.left || 0
      }px`,
      top: `${
        (crop.top * this.imageRef.width) /
          (this.imageRef.naturalWidth / this.props.imageZoomFactor) +
          offset.top || 0
      }px`
    };
  }

  showBoundingBoxes() {
    this.setState({ showBoundingBoxes: true });
  }

  keyForAnnotation(annotation) {
    return `${annotation.label}-${annotation.x}-${annotation.y}-${annotation.width}-${annotation.height}-${annotation.left}-${annotation.top}`;
  }

  getTooltip({ label, height, width, attributes }) {
    return (
      <div>
        {label} - {width}
        px by {height}
        px
        {attributes ? <span>- Attributes: {JSON.stringify(attributes)}</span> : undefined}
      </div>
    );
  }

  renderBoundingBox(annotation) {
    const boundingBoxStyle = this.percentageCropToStyle(annotation);
    const boundingBoxClassNames = cx('bounding-box', {
      'bounding-box--show-labels': this.state.alwaysShowLabels,
      hover: this.props.hover
    });

    return (
      <div
        className={boundingBoxClassNames}
        onMouseLeave={this.hideAnnotation}
        onMouseEnter={this.setActiveAnnotation(annotation)}
        key={this.keyForAnnotation(annotation)}
        style={boundingBoxStyle}
      >
        <div className="bounding-box__content">
          {annotation.attributes &&
            Object.keys(annotation.attributes).map(att => (
              <div
                key={att}
                className="bounding-box__label"
              >{`${att}: ${annotation.attributes[att]}`}</div>
            ))}
        </div>

        <style jsx>{`
          .bounding-box {
            opacity: 0;
            animation-name: fade;
            animation-duration: 0.5s;
            animation-fill-mode: both;
            animation-delay: 0.5s;
            transition: background 300ms;
            z-index: 10;
          }
          .bounding-box.hover:hover {
            background: rgba(127, 255, 127, 0.6);
            z-index: 12;
            cursor: default;
          }
          
          .bounding-box {
            position: absolute;
            border: 1px solid #7fff7f;
            display: flex;
            box-sizing: content-box;
            margin: -1px;
            background: rgba(127, 255, 127, 0.2);
          }
          
          .bounding-box__label {
            position: relative;
            top: 1px;
            left: 1px;
            white-space: nowrap;
            display: none;
            font-size: 10px;
            color: #7fff7f;
            text-shadow: 0 0 1px #333;
            z-index: 9999;
          }
          
          .bounding-box.bounding-box--show-labels .bounding-box__label {
            display: block;
          }
          
          .bounding-box:hover .bounding-box__label {
            display: block;
          }
          
          .bounding-box__content {
            position: relative;
            height: 100%;
            width: 100%;
          }
          
          @keyframes fade {
            from {
              opacity: 0;
            }
            to {
              opacity: 1;
            }
          }
        `}</style>
      </div>
    );
  }

  render() {
    const { tooltip } = this.state;

    return (
      <div style={{ position: 'relative' }} ref={ref => (this.content = ref)}>
        <div
          className={cx('task-response__wrapper', {
            'task-response__wrapper--rounded': this.props.rounded
          })}
        >
          <div
            className={cx('task-response__annotation', {
              'task-response__annotation--bg-transparent': this.props.bgTransparent,
              'task-response__annotation--max-height': !this.props.responsive
            })}
          >
            <div
              style={{
                position: 'relative',
                margin: '0 auto',
                ...(this.props.responsive ? {} : { maxHeight: 500 })
              }}
            >
              <img
                key={this.props.imageURL}
                ref={ref => {
                  this.imageRef = ref;
                }}
                onLoad={this.showBoundingBoxes}
                className={cx('task-response__annotation-image', {
                  'task-response__annotation-image--max-height': !this.props.responsive,
                  'task-response__annotation-image--responsive': this.props.responsive,
                  'task-response__annotation-image--transparent': this.props.transparent
                })}
                src={this.props.imageURL}
                style={this.imageStyle}
                alt={this.props.imageURL}
              />
              {this.state.showBoundingBoxes && this.props.annotations.map(this.renderBoundingBox)}
            </div>
          </div>
        </div>
        {this.state.showBoundingBoxes && <Tooltip {...tooltip} />}
        <style jsx>{`
          .task-response__wrapper--rounded {
            overflow: hidden;
            border-radius: var(--border-radius);
          }
          
          .task-response__annotation {
            position: relative;
            width: 100%;
            background-color: #dcdcdc;
          }
          
          .task-response__annotation--max-height {
            max-height: 500px;
          }
          
          .task-response__annotation--bg-transparent {
            background-color: transparent;
          }
          
          .task-response__annotation-image--transparent {
            opacity: 0;
          }
          
          .task-response__annotation-image--max-height {
            max-height: 500px;
          }
          
          .task-response__annotation-image--responsive {
            width: 100%;
          }
        `}</style>
      </div>
    );
  }
}
